import type { InferOutput } from 'valibot'
import { array, boolean, literal, nullish, number, object, optional, pipe, string, transform, union } from 'valibot'
import { lastUpdatedByEditor, publish_date } from '#shared/utils/solr-fields'
import { normalizeJuridikaImageUrl } from './juridika-media-image'
import { createSolrResponseSchema } from './schemas'

/**
 *  Schema for author works, which can be of various document types such as book, academic article, journal article, or journal editorial
 */
const authorWorkNodeType = union([
  literal('book'),
  literal('academic_article'),
  literal('journal_article'),
  literal('journal_editorial'),
])

/**
 *  Base schema for author works, capturing common fields across different document types
 */
export const baseAuthorWorkSchema = object({
  id: string(),
  title: nullish(string()),
  nodeType: authorWorkNodeType,
  coverImage: optional(union([string(), array(string())])),
  year: optional(string()),
  dateOfPublication: optional(string()),
  lastUpdatedByEditor,
  publish_date,
  author: optional(array(string())),
  authorName: optional(array(string())),
  author_list: optional(array(string())),
  remoteId: optional(string()),
  publisherId: optional(string()),
  volume: optional(string()),
  issue: optional(string()),
  editionNumber: nullish(string()),
  subtitle: nullish(string()),
  lastEdition: nullish(boolean()),
  journalId: optional(string()),
  journalTitle: optional(string()), // enriched from parent journal on server
  ezMainNodeId: optional(number()), // needed for academic article section enrichment
  bodytext: optional(string()), // enriched from academic_article_section on server

})

export type BaseAuthorWork = InferOutput<typeof baseAuthorWorkSchema>

/**
 * Schema for author works returned from Solr, transforming and normalizing fields as needed
 */
export const authorWorkSchema = pipe(
  baseAuthorWorkSchema,
  transform((doc) => {
    const coverImage = normalizeJuridikaImageUrl(doc.coverImage)

    return {
      id: doc.id,
      title: doc.title ?? '',
      nodeType: doc.nodeType,
      coverImage,
      date: getSolrDocumentDate(doc),
      authors: getSolrDocumentAuthor(doc),
      remoteId: doc.remoteId,
      publisherId: doc.publisherId,
      volume: doc.volume,
      issue: doc.issue,
      editionNumber: doc.editionNumber ?? undefined,
      year: doc.year,
      subTitle: doc.journalTitle || doc.subtitle || undefined,
      lastEdition: doc.lastEdition ?? undefined,
      journalId: doc.journalId,
      bodytext: doc.bodytext,
    }
  }),
)

export type AuthorWork = InferOutput<typeof authorWorkSchema>

/**
 * Schema for the Solr response when querying for an author's works, including pagination and metadata
 */
export const authorWorksResponseSchema = createSolrResponseSchema(authorWorkSchema)
export type AuthorWorksResponse = InferOutput<typeof authorWorksResponseSchema>
